import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { UserIcon, PackageIcon, HeartIcon, MapPinIcon, SettingsIcon, LogOutIcon, ChevronRightIcon, EditIcon } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
const menuItems = [{
  icon: PackageIcon,
  label: 'My Orders',
  href: '/account/orders',
  count: 5
}, {
  icon: HeartIcon,
  label: 'Wishlist',
  href: '/wishlist',
  count: 12
}, {
  icon: MapPinIcon,
  label: 'Addresses',
  href: '/account/addresses'
}, {
  icon: SettingsIcon,
  label: 'Settings',
  href: '/account/settings'
}];
export function AccountPage() {
  const navigate = useNavigate();
  const {
    user,
    logout,
    isAuthenticated
  } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  if (!isAuthenticated) {
    navigate('/login');
    return null;
  }
  const handleLogout = () => {
    logout();
    navigate('/');
  };
  return <div<div> className="max-w-7xl mx-auto px-4" data-id="element-906">
      <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6" data-id="element-907">
        My Account
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-908">
        {/* Profile card */}
        <div className="lg:col-span-1" data-id="element-909">
          <Card padding="lg" data-id="element-910">
            <div className="text-center" data-id="element-911">
              <div className="w-24 h-24 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4 overflow-hidden" data-id="element-912">
                <img src="https://cdn.magicpatterns.com/uploads/fRsBpVfPoCHsQawYgHjccg/ChatGPT_Image_Mar_6,_2026,_03_48_02_AM.png" alt="User Avatar" className="w-full h-full object-cover p-2" data-id="element-913" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900" data-id="element-914">
                {user?.name}
              </h2>
              <p className="text-gray-500" data-id="element-915">{user?.email}</p>
              <p className="text-sm text-gray-400 mt-1" data-id="element-916">
                Member since Jan 2024
              </p>
            </div>

            <div className="mt-6 space-y-2" data-id="element-917">
              {menuItems.map(item => <Link key={item.label} to={item.href} className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-918">
                  <div className="flex items-center gap-3" data-id="element-919">
                    <item.icon className="w-5 h-5 text-gray-500" data-id="element-920" />
                    <span className="font-medium text-gray-700" data-id="element-921">
                      {item.label}
                    </span>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-922">
                    {item.count && <span className="px-2 py-0.5 bg-primary-100 text-primary-600 text-xs font-medium rounded-full" data-id="element-923">
                        {item.count}
                      </span>}
                    <ChevronRightIcon className="w-4 h-4 text-gray-400" data-id="element-924" />
                  </div>
                </Link>)}

              <button onClick={handleLogout} className="flex items-center gap-3 p-3 rounded-lg hover:bg-red-50 transition-colors w-full text-left text-red-600" data-id="element-925">
                <LogOutIcon className="w-5 h-5" data-id="element-926" />
                <span className="font-medium" data-id="element-927">Logout</span>
              </button>
            </div>
          </Card>
        </div>

        {/* Main content */}
        <div className="lg:col-span-2 space-y-6" data-id="element-928">
          {/* Profile details */}
          <Card padding="lg" data-id="element-929">
            <div className="flex items-center justify-between mb-6" data-id="element-930">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-931">
                Profile Details
              </h3>
              <Button variant="ghost" size="sm" leftIcon={<EditIcon className="w-4 h-4" data-id="element-933" />} onClick={() => setIsEditing(!isEditing)} data-id="element-932">
                {isEditing ? 'Cancel' : 'Edit'}
              </Button>
            </div>

            {isEditing ? <form className="space-y-4" data-id="element-934">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-935">
                  <Input label="Full Name" defaultValue={user?.name} data-id="element-936" />
                  <Input label="Phone" defaultValue={user?.phone} data-id="element-937" />
                  <Input label="Email" type="email" defaultValue={user?.email} className="md:col-span-2" data-id="element-938" />
                </div>
                <div className="flex gap-3" data-id="element-939">
                  <Button variant="primary" type="submit" data-id="element-940">
                    Save Changes
                  </Button>
                  <Button variant="outline" onClick={() => setIsEditing(false)} data-id="element-941">
                    Cancel
                  </Button>
                </div>
              </form> : <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-942">
                <div data-id="element-943">
                  <p className="text-sm text-gray-500" data-id="element-944">Full Name</p>
                  <p className="font-medium text-gray-900" data-id="element-945">{user?.name}</p>
                </div>
                <div data-id="element-946">
                  <p className="text-sm text-gray-500" data-id="element-947">Phone</p>
                  <p className="font-medium text-gray-900" data-id="element-948">{user?.phone}</p>
                </div>
                <div className="md:col-span-2" data-id="element-949">
                  <p className="text-sm text-gray-500" data-id="element-950">Email</p>
                  <p className="font-medium text-gray-900" data-id="element-951">{user?.email}</p>
                </div>
              </div>}
          </Card>

          {/* Recent orders */}
          <Card padding="lg" data-id="element-952">
            <div className="flex items-center justify-between mb-6" data-id="element-953">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-954">
                Recent Orders
              </h3>
              <Link to="/account/orders" className="text-primary-600 hover:text-primary-700 text-sm font-medium" data-id="element-955">
                View All
              </Link>
            </div>

            <div className="space-y-4" data-id="element-956">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg" data-id="element-957">
                <div className="flex items-center gap-4" data-id="element-958">
                  <div className="w-16 h-16 bg-white rounded-lg flex items-center justify-center" data-id="element-959">
                    <PackageIcon className="w-8 h-8 text-gray-400" data-id="element-960" />
                  </div>
                  <div data-id="element-961">
                    <p className="font-medium text-gray-900" data-id="element-962">
                      Order #GZ-ABC123
                    </p>
                    <p className="text-sm text-gray-500" data-id="element-963">
                      2 items • PKR 45,999
                    </p>
                    <p className="text-xs text-gray-400" data-id="element-964">
                      Placed on Jan 15, 2024
                    </p>
                  </div>
                </div>
                <div className="text-right" data-id="element-965">
                  <span className="px-3 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-full" data-id="element-966">
                    Delivered
                  </span>
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg" data-id="element-967">
                <div className="flex items-center gap-4" data-id="element-968">
                  <div className="w-16 h-16 bg-white rounded-lg flex items-center justify-center" data-id="element-969">
                    <PackageIcon className="w-8 h-8 text-gray-400" data-id="element-970" />
                  </div>
                  <div data-id="element-971">
                    <p className="font-medium text-gray-900" data-id="element-972">
                      Order #GZ-DEF456
                    </p>
                    <p className="text-sm text-gray-500" data-id="element-973">1 item • PKR 12,999</p>
                    <p className="text-xs text-gray-400" data-id="element-974">
                      Placed on Jan 20, 2024
                    </p>
                  </div>
                </div>
                <div className="text-right" data-id="element-975">
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 text-sm font-medium rounded-full" data-id="element-976">
                    In Transit
                  </span>
                </div>
              </div>
            </div>
          </Card>

          {/* Saved addresses */}
          <Card padding="lg" data-id="element-977">
            <div className="flex items-center justify-between mb-6" data-id="element-978">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-979">
                Saved Addresses
              </h3>
              <Button variant="outline" size="sm" data-id="element-980">
                Add New
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-981">
              {user?.addresses.map(address => <div key={address.id} className={`p-4 rounded-lg border-2 ${address.isDefault ? 'border-primary-600 bg-primary-50' : 'border-gray-200'}`} data-id="element-982">
                  {address.isDefault && <span className="text-xs font-medium text-primary-600 mb-2 block" data-id="element-983">
                      Default
                    </span>}
                  <p className="font-medium text-gray-900" data-id="element-984">{address.name}</p>
                  <p className="text-sm text-gray-600 mt-1" data-id="element-985">
                    {address.address}
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-986">
                    {address.city}, {address.postalCode}
                  </p>
                  <p className="text-sm text-gray-500 mt-2" data-id="element-987">{address.phone}</p>
                </div>)}
            </div>
          </Card>
        </div>
      </div>
    </div>;
  <div data-id="element-988" />;
}